import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Layout, Tabs, Rate } from "antd";
import SockJS from "sockjs-client";
import { CompatClient, Stomp } from "@stomp/stompjs";

import {
  selectIsPerfumeLoaded,
  selectIsPerfumeLoading,
  selectPerfume,
  selectPerfumeError,
  selectPerfumeErrorMessage,
  selectReviews,
} from "../../redux-toolkit/perfume/perfume-selector";
import { selectReviewErrors } from "../../redux-toolkit/user/user-selector";
import {
  fetchPerfume,
  fetchReviewsByPerfumeId,
} from "../../redux-toolkit/perfume/perfume-thunks";
import { resetInputForm } from "../../redux-toolkit/user/user-slice";
import { WEBSOCKET_URL } from "../../constants/urlConstants";
import {
  resetPerfumeState,
  setReview,
} from "../../redux-toolkit/perfume/perfume-slice";
import { addReviewToPerfume } from "../../redux-toolkit/user/user-thunks";
import { useCart } from "../../hooks/useCart";

import Spinner from "../../components/Spinner/Spinner";
import ErrorMessage from "./ErrorMessage/ErrorMessage";
import ProductReviews from "./ProductReviews/ProductReviews";

import "./Product.css";

let stompClient: CompatClient | null = null;
const { TabPane } = Tabs;

const Product: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();

  const perfume = useSelector(selectPerfume);
  const reviews = useSelector(selectReviews);
  const isPerfumeLoading = useSelector(selectIsPerfumeLoading);
  const isPerfumeLoaded = useSelector(selectIsPerfumeLoaded);
  const isPerfumeError = useSelector(selectPerfumeError);
  const errorMessage = useSelector(selectPerfumeErrorMessage);
  const reviewErrors = useSelector(selectReviewErrors);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [isDistributor, setIsDistributor] = useState<boolean>(false);
  const { addToCart } = useCart(perfume);

  useEffect(() => {
    const role = localStorage.getItem("role");
    setIsDistributor(role === "3");
  }, []);

  useEffect(() => {
    if (params.id) {
      dispatch(fetchPerfume(params.id));
      dispatch(resetInputForm());
      window.scrollTo(0, 0);
    }

    try {
      stompClient = Stomp.over(() => new SockJS(WEBSOCKET_URL));
      stompClient.connect({}, () => {
        if (params.id) {
          stompClient?.subscribe(`/topic/reviews/${params.id}`, (response: any) => {
            dispatch(setReview(JSON.parse(response.body)));
          });
        }
      });
    } catch (e) {}

    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);

    return () => {
      stompClient?.disconnect();
      dispatch(resetPerfumeState());
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (isPerfumeLoaded && params.id) {
      dispatch(fetchReviewsByPerfumeId(params.id));
    }
  }, [isPerfumeLoaded, dispatch, params.id]);

  const displayPrice = isDistributor ? perfume?.distributorPrice : perfume?.price;
  const displayDiscount = isDistributor ? perfume?.distributorDiscount : perfume?.discount;

  const handleAddToCart = () => {
    addToCart();
    window.location.href = "/cart";
  };

  const handleBuyNow = () => {
    addToCart();
    window.location.href = "/cart";
  };

  if (isPerfumeLoading) return <Spinner />;
  if (isPerfumeError) return <ErrorMessage errorMessage={errorMessage} />;

  return (
    <>
      <Layout className="fk-page-layout">
        <Layout.Content className="fk-page-content">

        {/* BREADCRUMB */}
        <div className="fk-breadcrumb">
          <span>Home</span> &rsaquo;
          <span>{perfume?.category || "Products"}</span> &rsaquo;
          <span className="fk-breadcrumb-current">{perfume?.perfumeTitle}</span>
        </div>

        {/* MAIN PRODUCT CARD */}
        <div className="fk-product-card">

          {/* LEFT: IMAGE + CTA */}
          <div className="fk-img-panel">
            <div className="fk-img-box">
              <img
                src={perfume?.filename || "/no-image.png"}
                alt={perfume?.perfumeTitle}
                className="fk-product-img"
              />
            </div>

            <div className="fk-cta-wrap">
              <button className="fk-btn-cart" onClick={handleAddToCart}>
                🛒&nbsp; ADD TO CART
              </button>
              <button className="fk-btn-buy" onClick={handleBuyNow}>
                ⚡&nbsp; BUY NOW
              </button>
            </div>
          </div>

          {/* RIGHT: DETAILS */}
          <div className="fk-detail">
            <div className="fk-brand">{perfume?.brand}</div>
            <h1 className="fk-title">{perfume?.perfumeTitle}</h1>

            {/* RATING ROW */}
            <div className="fk-rating-row">
              <span className="fk-rating-pill">
                {perfume?.perfumeRating || 0} ★
              </span>
              <Rate
                allowHalf
                disabled
                value={perfume?.perfumeRating || 0}
                className="fk-stars"
              />
              <span className="fk-rating-meta">{reviews?.length || 0} Reviews</span>
            </div>

            {/* PRICE ROW */}
            <div className="fk-price-row">
              <span className="fk-price">₹{displayPrice}</span>
              {perfume?.actualPrice && (
                <span className="fk-mrp">₹{perfume.actualPrice}</span>
              )}
              {displayDiscount && displayDiscount > 0 && (
                <span className="fk-off">({displayDiscount}% OFF)</span>
              )}
              {isDistributor && (
                <span className="fk-dist-badge">Distributor Price</span>
              )}
            </div>

            {/* MIN QTY */}
            {perfume?.discount && perfume.discount > 0 && (
              <div className="fk-min-qty">
                Min. Qty: <span>{perfume.minQuantity}</span>
              </div>
            )}

            <hr className="fk-divider" />

            {/* DELIVERY */}
            <div className="fk-delivery-row">
              <span className="fk-delivery-icon">🚚</span>
              <span className="fk-delivery-text">
                <strong>Free Delivery</strong>
                &nbsp;·&nbsp;
                {perfume?.deliveryTimeline || "Within 7 Days"}
              </span>
            </div>

            {/* SELLER */}
            {perfume?.sellerName && (
              <div className="fk-seller-row">
                <span>Seller:</span> {perfume.sellerName}
              </div>
            )}

            <hr className="fk-divider" />

            {/* PRODUCT HIGHLIGHTS */}
            <div className="fk-spec-title">Product Details</div>
            <table className="fk-spec-table">
              {perfume?.brand && (
                <tr><td>Brand</td><td>{perfume.brand}</td></tr>
              )}
              {perfume?.category && (
                <tr><td>Category</td><td>{perfume.category}</td></tr>
              )}
              {perfume?.subCategory && (
                <tr><td>Sub Category</td><td>{perfume.subCategory}</td></tr>
              )}
              {perfume?.unit && (
                <tr><td>Unit</td><td>{perfume.unit}</td></tr>
              )}
              {perfume?.sellerName && (
                <tr><td>Seller</td><td>{perfume.sellerName}</td></tr>
              )}
              {perfume?.deliveryTimeline && (
                <tr><td>Delivery</td><td>{perfume.deliveryTimeline}</td></tr>
              )}
            </table>
          </div>
        </div>

        {/* TABS */}
        <div className="fk-tabs-wrap">
          <Tabs defaultActiveKey="details" type="line" size="large" className="fk-tabs">
            <TabPane tab="Details" key="details">
              <p className="fk-desc-text">
                {perfume?.description || "No description available."}
              </p>
              <div className="fk-desc-grid">
                {perfume?.brand && (
                  <div className="fk-desc-cell"><b>Brand</b><span>{perfume.brand}</span></div>
                )}
                {perfume?.category && (
                  <div className="fk-desc-cell"><b>Category</b><span>{perfume.category}</span></div>
                )}
                {perfume?.subCategory && (
                  <div className="fk-desc-cell"><b>Sub Category</b><span>{perfume.subCategory}</span></div>
                )}
                {perfume?.unit && (
                  <div className="fk-desc-cell"><b>Unit</b><span>{perfume.unit}</span></div>
                )}
                {perfume?.sellerName && (
                  <div className="fk-desc-cell"><b>Seller</b><span>{perfume.sellerName}</span></div>
                )}
                {perfume?.deliveryTimeline && (
                  <div className="fk-desc-cell"><b>Delivery</b><span>{perfume.deliveryTimeline}</span></div>
                )}
              </div>
            </TabPane>

            <TabPane tab={`Reviews (${reviews?.length || 0})`} key="reviews">
              <ProductReviews
                reviews={reviews}
                reviewErrors={reviewErrors}
                addReview={async (d) => {
                  await dispatch(addReviewToPerfume({ perfumeId: params.id!, ...d }));
                }}
              />
            </TabPane>
          </Tabs>
        </div>

      </Layout.Content>
    </Layout>

    {/* MOBILE STICKY BOTTOM BAR — always in DOM, shown/hidden via CSS only */}
    <div className="fk-mobile-sticky">
      <button className="fk-btn-cart" onClick={handleAddToCart}>
        🛒 ADD TO CART
      </button>
      <button className="fk-btn-buy" onClick={handleBuyNow}>
        ⚡ BUY NOW
      </button>
    </div>
    </>
  );
};

export default Product;